import React from 'react';
import { API_BASE_URL } from '../../services/api';
import PerformanceChart from './charts/PerformanceChart';
import { fyFooterWithPartial } from '../../utils/performanceDisclosure';
import CreditExposureChart from './charts/CreditExposureChart';

const DEFAULT_IMPORTANT_NOTICE = "1: Includes Drummond IM and RE fees. Fees may vary by platform. 2: Includes underlying net MER, performance and transaction costs. Costs may vary by platform. 3: Portfolios are capped at a net MER of 0.50% p.a., excluding performance and transaction costs. (Source: BT Panorama Platform PDS)\n\nThis information is based on the latest available data provided by the underlying investment managers. Returns greater than one year are annualised. Performance is shown net of underlying fund manager fees and gross of model management fees and is calculated from the model portfolio within BT Panorama. Actual client performance may differ over time.\n\nThis document has been prepared by Drummond Capital Partners (ABN 15 622 660 182, AFSL 534213) and contains general information only. It does not take into account the objectives, financial situation, or needs of any investor and does not constitute personal advice, a product disclosure statement, or any other disclosure document under the Corporations Act 2001 (Cth). This document has not been lodged with ASIC.\n\nTo the maximum extent permitted by law, no representation or warranty is made as to the accuracy or completeness of the information. Investors should seek their own professional advice before making any investment decision.";

// Convert text to title case (e.g. "BHP GROUP" → "Bhp Group")
const toTitleCase = (str) => {
  if (!str) return str;
  return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

// Asset class display order for the allocation table
const ASSET_CLASS_ORDER = [
  'Australian Equities',
  'International Equities',
  'EM Equities',
  'Real Estate',
  'Infrastructure',
  'Cash',
  'Bonds',
  'Credit'
];

// Group holdings by asset class for the allocation table
const groupHoldingsByAssetClass = (holdings) => {
  const groups = {};
  holdings.forEach(h => {
    const assetClass = h.asset_class || 'Other';
    if (!groups[assetClass]) {
      groups[assetClass] = { name: assetClass, funds: [], total: 0 };
    }
    groups[assetClass].funds.push(h);
    groups[assetClass].total += h.weight;
  });

  const sortedGroups = Object.values(groups).sort((a, b) => {
    const indexA = ASSET_CLASS_ORDER.indexOf(a.name);
    const indexB = ASSET_CLASS_ORDER.indexOf(b.name);
    const orderA = indexA === -1 ? 999 : indexA;
    const orderB = indexB === -1 ? 999 : indexB;
    return orderA - orderB;
  });

  return sortedGroups;
};

const FactSheetPage2 = ({ data, theme, chartRefs }) => {
  const { portfolio, performance, holdings, time_series, branding, content } = data;
  const creditExposure = data.credit_exposure;
  const volatilityData = data.volatility_data;
  // If branding exists (branded portfolio), use brand logo or nothing; otherwise Drummond default
  const rawLogoUrl = branding ? branding.logo_url : (theme.logo_url || '/logo-name.png');
  // Resolve relative API paths to absolute URLs (needed when frontend/backend are on different ports)
  const logoUrl = rawLogoUrl && rawLogoUrl.startsWith('/api') ? `${API_BASE_URL}${rawLogoUrl}` : rawLogoUrl;

  // Group holdings for asset allocation table
  const allocationGroups = groupHoldingsByAssetClass(holdings);

  // Get top 10 holdings from lookthrough table
  const topHoldings = data.top_10_lookthrough || [];

  // Transposed performance columns — only include where portfolio data exists
  const allPerfColumns = [
    { label: '1m', portfolio: performance.return_1m, peer: performance.peer_1m },
    { label: '3m', portfolio: performance.return_3m, peer: performance.peer_3m },
    { label: '6m', portfolio: performance.return_6m, peer: performance.peer_6m },
    { label: '1yr.', portfolio: performance.return_1y, peer: performance.peer_1y },
    { label: '3yr', portfolio: performance.return_3y, peer: performance.peer_3y },
    { label: '5yr', portfolio: performance.return_5y, peer: performance.peer_5y },
    { label: '7yr', portfolio: performance.return_7y, peer: performance.peer_7y },
    { label: '10yr', portfolio: performance.return_10y, peer: performance.peer_10y },
    { label: 'S.I.(p.a.)', portfolio: performance.return_si, peer: performance.peer_si },
    { label: 'Vol.(p.a.)', portfolio: volatilityData?.portfolio, peer: volatilityData?.peer, isVol: true },
  ];
  const perfColumns = allPerfColumns.filter(col => col.portfolio != null);
  const fmt = (v) => (v != null ? `${v.toFixed(1)}%` : '\u2014');
  const peerName = time_series?.benchmark_name || 'FE Growth Peer Index';
  // Only show the Performance Since Inception chart when the portfolio's own series
  // carries actual data — hide it when every period is 0.0% (or missing).
  const hasPortfolioPerformance = Array.isArray(time_series?.portfolio)
    && time_series.portfolio.some((d) => d && d.value != null && Math.abs(d.value) > 1e-9);
  const perfAsAt = time_series?.portfolio?.length > 0
    ? new Date(time_series.portfolio[time_series.portfolio.length - 1].date)
        .toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase()
    : null;
  // FY returns — only columns with data, partial years flagged
  const fyData = data.fy_returns || {};
  const fyColumns = (fyData.columns || []);
  const hasPartialFY = fyColumns.some(c => c.partial);

  // Optional, editable headers/footers for the two performance tables.
  // A blank override falls back to the computed default; *_show toggles display.
  const fyHeadingDefault = 'Performance - Financial Year';

  // Performance heading: editable text followed by the automatic as-at date.
  const showPerfHeading = content?.perf_table_heading_show !== false;
  const perfPrefix = (content?.perf_table_heading || '').trim() || 'Performance';
  const perfHeadingText = perfAsAt ? `${perfPrefix} – ${perfAsAt}` : perfPrefix;
  const showPerfFooter = content?.perf_table_footer_show === true;
  const perfFooterText = (content?.perf_table_footer || '').trim();
  const showFyHeading = content?.fy_table_heading_show !== false;
  const fyHeadingText = (content?.fy_table_heading || '').trim() || fyHeadingDefault;
  const showFyFooter = content?.fy_table_footer_show !== false;
  // This portfolio's own footer (as being edited) else the API-resolved text:
  // its inherited override or Admin > Disclaimers (perf_fy_footer).
  const fyFooterText = fyFooterWithPartial(
    (content?.fy_table_footer || '').trim() || content?.fy_table_footer_text, hasPartialFY);

  return (
    <div className="fact-sheet-page page-2">
      {/* Header */}
      <header className="fs-header">
        <div className="header-content">
          <div className="header-left">
            <h1 className="header-title">{portfolio.name}</h1>
            <p className="header-subtitle">FACT SHEET</p>
          </div>
          <div className="header-right">
            {logoUrl && (
              <div className="logo">
                <img src={logoUrl} alt="Logo" />
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="fs-content">

        {/* 1. Transposed Performance Table (full width) — hidden when no real data */}
        {perfColumns.length > 0 && (
          <>
            {showPerfHeading && (
              <h2 className="section-heading">{perfHeadingText}</h2>
            )}
            <table className="performance-table-transposed">
              <thead>
                <tr>
                  <th className="label-col"></th>
                  {perfColumns.map((col, i) => (
                    <th key={i} className={col.isVol ? 'vol-header' : ''}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="label-col portfolio-name-cell">{portfolio.name}</td>
                  {perfColumns.map((col, i) => (
                    <td key={i}>{fmt(col.portfolio)}</td>
                  ))}
                </tr>
                <tr>
                  <td className="label-col">{peerName}</td>
                  {perfColumns.map((col, i) => (
                    <td key={i}>{fmt(col.peer)}</td>
                  ))}
                </tr>
                <tr className="difference-row">
                  <td className="label-col">Difference</td>
                  {perfColumns.map((col, i) => {
                    if (col.portfolio != null && col.peer != null) {
                      const diff = col.portfolio - col.peer;
                      const isPositive = col.isVol ? diff <= 0 : diff >= 0;
                      return (
                        <td key={i} className={isPositive ? 'positive-diff' : ''}>
                          {diff.toFixed(1)}%
                        </td>
                      );
                    }
                    return <td key={i}>{'\u2014'}</td>;
                  })}
                </tr>
              </tbody>
            </table>
            {showPerfFooter && perfFooterText && (
              <p className="perf-note">{perfFooterText}</p>
            )}
          </>
        )}

        {/* 2. Financial Year Performance Table (full width) */}
        {fyColumns.length > 0 && (
          <>
            {showFyHeading && (
              <h2 className="section-heading">{fyHeadingText}</h2>
            )}
            <table className="performance-table-transposed">
              <thead>
                <tr>
                  <th className="label-col"></th>
                  {fyColumns.map((col, i) => (
                    <th key={i}>{col.partial ? `${col.label}*` : col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="label-col portfolio-name-cell">{portfolio.name}</td>
                  {fyColumns.map((col, i) => (
                    <td key={i}>{fmt(col.portfolio)}</td>
                  ))}
                </tr>
                <tr>
                  <td className="label-col">{peerName}</td>
                  {fyColumns.map((col, i) => (
                    <td key={i}>{fmt(col.peer)}</td>
                  ))}
                </tr>
                <tr className="difference-row">
                  <td className="label-col">Difference</td>
                  {fyColumns.map((col, i) => {
                    if (col.portfolio != null && col.peer != null) {
                      const diff = col.portfolio - col.peer;
                      return (
                        <td key={i} className={diff >= 0 ? 'positive-diff' : ''}>
                          {diff.toFixed(1)}%
                        </td>
                      );
                    }
                    return <td key={i}>{'\u2014'}</td>;
                  })}
                </tr>
              </tbody>
            </table>
            {showFyFooter && fyFooterText && (
              <p className="perf-note">{fyFooterText}</p>
            )}
          </>
        )}

        {/* Card wrapping holdings, top 10, credit exposure and performance chart */}
        <div className="page2-card">

        {/* 3. Two-column: Asset Allocation (left) | Top 10 Holdings + Credit (right) */}
        <div className="two-column">
          {/* Left Column */}
          <div className="left-column">
            {allocationGroups.length > 0 && (
              <>
                {/* Dated to the day of viewing (Aaron 2026-08-25): the table shows the
                    LIVE approved target set — same convention as the PDF template and
                    the Allocation Calculator. */}
                <h2 className="section-heading">
                  Target Weights &ndash; {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
                </h2>
                <table className="data-table holdings-table">
                  <colgroup>
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '60%' }} />
                    <col style={{ width: '18%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Asset Class</th>
                      <th>Fund</th>
                      <th className="right">Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allocationGroups.map((group, gIdx) => (
                      <React.Fragment key={gIdx}>
                        {group.funds.map((fund, fIdx) => (
                          <tr key={fIdx} className={fIdx === group.funds.length - 1 ? 'group-last-row' : ''}>
                            {fIdx === 0 && (
                              <td className="asset-class-cell" rowSpan={group.funds.length}>{group.name}</td>
                            )}
                            <td>{fund.name}</td>
                            <td className="right">{fund.weight.toFixed(1)}%</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>

          {/* Right Column */}
          <div className="right-column">
            {/* Top 10 Holdings */}
            {topHoldings.length > 0 && (
              <>
                <h2 className="section-heading">Top 10 Stock Holdings</h2>
                <table className="data-table">
                  <colgroup>
                    <col style={{ width: '65%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '17%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th className="center">Country</th>
                      <th className="center">Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topHoldings.map((holding, idx) => (
                      <tr key={idx}>
                        <td>{toTitleCase(holding.name)}</td>
                        <td className="center">{holding.country || holding.asset_class || '-'}</td>
                        <td className="center">{holding.weight.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Portfolio Credit Grade Exposure */}
            {creditExposure && Object.keys(creditExposure).length > 0 && (
              <>
                <h2 className="section-heading">Credit Grade Exposure</h2>
                <div className="chart-container credit-chart-container">
                  <CreditExposureChart ref={chartRefs?.credit} creditExposure={creditExposure} />
                </div>
              </>
            )}

            {/* Footnote for ^ markers — show per-table dates from the actual data */}
            {(data.top_10_as_of_date || data.credit_as_of_date) && (
              <p className="perf-note" style={{ marginTop: '2px' }}>
                {data.top_10_as_of_date && data.credit_as_of_date && data.top_10_as_of_date === data.credit_as_of_date
                  ? `As at ${data.top_10_as_of_date}`
                  : [
                      data.top_10_as_of_date && topHoldings.length > 0 && `Top 10 Stock Holdings as at ${data.top_10_as_of_date}`,
                      data.credit_as_of_date && creditExposure && Object.keys(creditExposure).length > 0 && `Credit Grade Exposure as at ${data.credit_as_of_date}`,
                    ].filter(Boolean).join('. ')
                }
              </p>
            )}
          </div>
        </div>

        {/* 4. Performance Since Inception chart (full width) — hidden when no
            cumulative time-series data. Decoupled from perfColumns so a missing
            perf-periods table no longer hides the chart by association. */}
        {hasPortfolioPerformance && (
          <div className="performance-chart-fullwidth">
            <h2 className="section-heading">Performance Since Inception</h2>
            <div className="chart-container">
              <PerformanceChart
                ref={chartRefs?.performance}
                timeSeries={time_series}
                portfolioName={portfolio.name}
              />
            </div>
          </div>
        )}

        </div>{/* end page2-card */}

        {/* 5. Important Notice */}
        <div className="important-notice">
          <h4>IMPORTANT NOTICE</h4>
          {(content?.important_notice || DEFAULT_IMPORTANT_NOTICE).split(/\n\n+/).map((para, i) => (
            <p key={i}>
              {para.split('\n').map((line, j, arr) => (
                <React.Fragment key={j}>
                  {line}
                  {j < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="fs-footer">
        <div className="footer-left">DRUMMOND CAPITAL PARTNERS</div>
        <div className="footer-right">PAGE 2</div>
      </footer>
    </div>
  );
};

export default FactSheetPage2;
